import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO_SLIDES, scrollToSection } from "../lib/site";

const SLIDE_MS = 6500;

export default function Hero() {
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % HERO_SLIDES.length), SLIDE_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" data-testid="hero-section" ref={ref} className="relative h-screen min-h-[640px] overflow-hidden">
      <AnimatePresence mode="sync">
        {HERO_SLIDES.map((s, i) =>
          i === active ? (
            <motion.div key={i} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.4, ease: "easeInOut" }}>
              <motion.img
                src={s.url}
                alt={s.alt}
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ scale: 1.0 }}
                animate={{ scale: 1.15 }}
                transition={{ duration: SLIDE_MS / 1000 + 1.5, ease: "linear" }}
              />
            </motion.div>
          ) : null
        )}
      </AnimatePresence>
      <div className="absolute inset-0 hero-vignette" />

      <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-28 sm:pb-32">
        <motion.p
          className="eyebrow mb-6"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
        >
         
        </motion.p>

        <h1 data-testid="hero-headline" className="font-display font-semibold text-white leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-8xl">
          <span className="masked-line">
            <motion.span animate={{ y: 0 }} initial={{ y: "110%" }} transition={{ delay: 0.55, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
              Your online
            </motion.span>
          </span>
          <span className="masked-line">
            <motion.span
              animate={{ y: 0 }}
              initial={{ y: "110%" }}
              transition={{ delay: 0.75, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="italic text-amber-500 amber-glow-text"
            >
              cashier.
            </motion.span>
          </span>
        </h1>

        <motion.p
          data-testid="hero-subline"
          className="mt-7 max-w-xl text-base sm:text-lg text-gray-300 leading-relaxed"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
        >
          Turn your counter into an automated ordering machine — online ordering, order management,
          delivery tracking and analytics for kebab shops, fish &amp; chips, takeaways, cafés and
          restaurants. Zero commission.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.8 }}
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollToSection("contact")}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0b0c10] font-bold px-8 py-4 rounded-full shadow-[0_0_30px_rgba(245,158,11,0.45)] transition-colors duration-200"
          >
            Start Free Demo
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToSection("products")}
            className="inline-flex items-center gap-2 border border-amber-500/40 hover:border-amber-400 bg-white/5 hover:bg-white/10 text-white font-medium px-8 py-4 rounded-full transition-colors duration-200"
          >
            Explore the Platform
          </button>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 inset-x-0 z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-end justify-between">
          <div data-testid="hero-slide-progress" className="flex gap-2">
            {HERO_SLIDES.map((_, i) => (
              <div key={i} className="h-[3px] w-12 rounded-full bg-white/15 overflow-hidden">
                {i === active && (
                  <motion.div
                    key={active}
                    className="h-full bg-amber-500"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: SLIDE_MS / 1000, ease: "linear" }}
                  />
                )}
              </div>
            ))}
          </div>
          <motion.div
            className="hidden sm:flex items-center gap-2 text-gray-400"
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          >
            <span className="font-mono-x text-[10px] tracking-[0.3em] uppercase">Scroll</span>
            <ArrowDown className="w-4 h-4 text-amber-500" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
